import type { ReactNode } from 'react';

/**
 * The verdict inside the "Your decision" panel (feature 023).
 *
 * A right answer needs no explanation, so it gets one line. A WRONG answer names the call
 * that was wanted — for the opening drills that is what Jeff actually bid, because "you
 * should not open" is not a lesson on its own: the learner needs to see what he did instead
 * (client decision, 2026-07-21). This detail used to live in the explain row below the
 * table; it belongs next to the verdict, where the learner is already looking.
 *
 * One line, "Answer: <call>", replaced the earlier pair of sentences ("The correct answer
 * is no" / "Jeff bid 1C") on 2026-07-21: restating a yes/no choice taught nothing, and the
 * call the learner needed was buried in the second line.
 *
 * A wrong answer also echoes the call the learner made, under the answer. On a call pad the
 * chosen bid is gone from the screen by the time the verdict appears, so "Answer: 2♥" alone
 * left them comparing it against memory (client decision, 2026-07-21).
 *
 * The block is a live region so the verdict is announced rather than silently swapped in.
 *
 * All three segments — the verdict, the answer and the echo — sit on ONE line (client request,
 * 2026-07-30). None of them is longer than a few words, so stacking them spent three lines of a
 * viewport-locked panel on about a line's worth of text, and on a phone that height came straight
 * off Jeff's commentary below. Each segment is `whitespace-nowrap` so a narrow column wraps
 * between them rather than splitting "You bid:" from the call it introduces.
 */

export interface ResultFeedbackProps {
  isCorrect: boolean;
  /** The call that was wanted, pre-rendered (bid tokens become suit symbols upstream). */
  answer: ReactNode;
  /**
   * The lead-in before the answer. Defaults to "Answer:". The opening drills pass a
   * self-contained phrase ("Jeff opened 1♥" / "Jeff passed") as {@link answer} and set this to
   * null so no label prefixes it (FR-017d).
   */
  label?: ReactNode | null;
  /**
   * Show the answer even on a CORRECT verdict. Set for the "no" drills, where being right
   * only means the learner ruled out the suggested opening — it never tells them what the
   * hand should have called instead (client decision, 2026-07-21).
   */
  revealWhenCorrect?: boolean;
  /**
   * The call the learner submitted, pre-rendered. Shown beneath the answer on a WRONG verdict
   * only — echoing it back when they were right would just repeat the line above.
   */
  userCall?: ReactNode;
}

export function ResultFeedback({
  isCorrect,
  answer,
  label = 'Answer:',
  revealWhenCorrect,
  userCall,
}: ResultFeedbackProps) {
  const showAnswer = !isCorrect || Boolean(revealWhenCorrect);
  const showUserCall = !isCorrect && userCall != null;

  return (
    <div
      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5"
      data-testid="result-feedback"
      role="status"
      aria-live="polite"
    >
      <p
        className={`whitespace-nowrap text-[15px] font-bold ${isCorrect ? 'text-primary-700' : 'text-red-700'}`}
        data-testid="result-verdict"
      >
        {isCorrect ? 'Correct!' : 'Incorrect!'}
      </p>

      {showAnswer && (
        <div
          className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[14px] text-ink"
          data-testid="result-detail"
        >
          <span className="whitespace-nowrap">
            {label != null && <span className="text-ink-soft">{label}</span>}
            {label != null && ' '}
            <span className="font-bold" data-testid="result-correct-answer">
              {answer}
            </span>
          </span>

          {showUserCall && (
            <span className="whitespace-nowrap">
              <span className="text-ink-soft">You bid:</span>{' '}
              <span className="font-bold" data-testid="result-user-call">
                {userCall}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
